import { closeArtifactAtom, openArtifactAtom } from '@heidi/agents/data';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback } from 'react';

import {
  RECENT_RUNS_LIMIT,
  recentRunsRailHiddenByUserAtom,
  runHasChatShell,
  useWorkflowRuns,
} from '@heidi/agents/data';

export interface RecentRunsRail {
  /** The routine has at least one openable run (a Work chat shell). */
  hasRuns: boolean;
  /** Whether the rail is currently on screen. */
  isOpen: boolean;
  /** Flip the rail; opening it closes the artifact canvas. */
  toggle: () => void;
}

/**
 * Open/closed state for the run chat's "Recent runs" card.
 *
 * Derived rather than stored, so there are no effects to keep in sync:
 *
 *   - Open by default once the routine has a run (the designed behaviour).
 *   - Mutually exclusive with the artifact canvas *in both directions*. The
 *     canvas → rail half is derived (``openArtifact === null``), so opening the
 *     configuration pane — by pencil, or by the agent auto-opening an artifact
 *     mid-run — hides the rail WITHOUT clobbering the user's own preference;
 *     closing the canvas brings the rail back. The rail → canvas half is the
 *     toggle, which closes the canvas as it opens the rail.
 *   - A user dismissal is remembered per routine
 *     ({@link recentRunsRailHiddenByUserAtom}) so it survives hopping between
 *     runs of that routine.
 */
export function useRecentRunsRail(workflowId: string): RecentRunsRail {
  // Same query key as the overview's Previous runs rail — one fetch.
  const { data: runsData } = useWorkflowRuns(workflowId, { limit: RECENT_RUNS_LIMIT });
  const hasRuns = (runsData?.runs ?? []).some(runHasChatShell);

  const openArtifact = useAtomValue(openArtifactAtom);
  const closeArtifact = useSetAtom(closeArtifactAtom);
  const hiddenByUser = useAtomValue(recentRunsRailHiddenByUserAtom);
  const setHiddenByUser = useSetAtom(recentRunsRailHiddenByUserAtom);

  const isOpen = hasRuns && openArtifact === null && !hiddenByUser[workflowId];

  const toggle = useCallback(() => {
    if (isOpen) {
      setHiddenByUser((prev) => ({ ...prev, [workflowId]: true }));
      return;
    }
    setHiddenByUser((prev) => ({ ...prev, [workflowId]: false }));
    // The rail and the canvas share the right margin — showing one hides the
    // other.
    closeArtifact();
  }, [isOpen, workflowId, setHiddenByUser, closeArtifact]);

  return { hasRuns, isOpen, toggle };
}
